import { apiClient } from './api'

// ========================================
// Types & Interfaces
// ========================================

export type PBSServer = {
  id: string
  name: string
  host: string
  port: number
  api_token_id: string
  verify_ssl: boolean
  connection_timeout: number
  is_active: boolean
  last_sync?: string
  version?: string
  total_storage_bytes?: number
  used_storage_bytes?: number
  available_storage_bytes?: number
  description?: string
  tags: string[]
  organization_id: string
  created_by: string
  created_at: string
  updated_at: string
}

export type PBSDatastore = {
  id: string
  name: string
  pbs_server_id: string
  path?: string
  total_bytes?: number
  used_bytes?: number
  available_bytes?: number
  gc_schedule?: string
  prune_schedule?: string
  verify_schedule?: string
  keep_last?: number
  keep_hourly?: number
  keep_daily?: number
  keep_weekly?: number
  keep_monthly?: number
  keep_yearly?: number
  is_active: boolean
  last_sync?: string
  description?: string
  created_at: string
  updated_at: string
}

export type ClusterPBSAssociation = {
  id: string
  cluster_id: string
  pbs_server_id: string
  priority: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export type PBSConnectionTest = {
  connected: boolean
  // Opcionais: a falha antes de chegar ao PBS responde só connected + error.
  server_id?: string
  server_name?: string
  cluster_id?: string
  host?: string
  version?: string
  release?: string
  status?: Record<string, any>
  error?: string
  timestamp: string
}

export type DatastoreUsageSummary = {
  datastore_id: string
  datastore_name: string
  total_capacity_bytes?: number
  used_bytes?: number
  available_bytes?: number
  snapshot_count: number
  backups_size_bytes: number
  utilization_percentage: number
}

// ========================================
// Request Schemas
// ========================================

export type CreatePBSServerRequest = {
  name: string
  host: string
  port?: number
  api_token_id: string
  api_token_secret: string
  verify_ssl?: boolean
  connection_timeout?: number
  description?: string
  tags?: string[]
  /**
   * Cluster por onde validar a conexão antes de criar. O backend só alcança o
   * PBS pelo agent de um cluster; sem ele o servidor é cadastrado sem validação.
   */
  cluster_id?: string
}

export type UpdatePBSServerRequest = {
  name?: string
  api_token_id?: string
  api_token_secret?: string
  verify_ssl?: boolean
  connection_timeout?: number
  description?: string
  tags?: string[]
  is_active?: boolean
}

export type UpdateDatastoreRequest = {
  gc_schedule?: string
  prune_schedule?: string
  verify_schedule?: string
  keep_last?: number
  keep_hourly?: number
  keep_daily?: number
  keep_weekly?: number
  keep_monthly?: number
  keep_yearly?: number
  description?: string
  is_active?: boolean
}

export type AssociateClusterPBSRequest = {
  cluster_id: string
  pbs_server_id: string
  priority?: number
  datastore_name?: string // Nome do datastore no PBS (opcional - usa primeiro disponível se não fornecido)
}

export type RunGarbageCollectionRequest = {
  datastore_name: string
}

/**
 * O endpoint devolve a LISTA de datastores sincronizados (`List[PBSDatastoreResponse]`
 * no backend), não um objeto com contadores. O tipo antigo prometia
 * `created_count`/`updated_count`, que nunca vieram — o toast do nebula-cp
 * mostrava "Criados: undefined".
 */
export type SyncDatastoresResponse = PBSDatastore[]

// ========================================
// API Client - PBS Server Management
// ========================================

export const pbsServerApi = {
  // ========================================
  // PBS Servers
  // ========================================

  /**
   * Lista servidores PBS do Cloud Provider
   */
  listServers: (params?: {
    is_active?: boolean
    skip?: number
    limit?: number
  }): Promise<PBSServer[]> => {
    return apiClient.get('/api/v1/pbs/pbs-servers', { params })
  },

  /**
   * Cria novo servidor PBS
   */
  createServer: (data: CreatePBSServerRequest): Promise<PBSServer> => {
    return apiClient.post('/api/v1/pbs/pbs-servers', data)
  },

  /**
   * Obtém detalhes de servidor PBS
   */
  getServer: (serverId: string): Promise<PBSServer> => {
    return apiClient.get(`/api/v1/pbs/pbs-servers/${serverId}`)
  },

  /**
   * Atualiza servidor PBS
   */
  updateServer: (serverId: string, data: UpdatePBSServerRequest): Promise<PBSServer> => {
    return apiClient.put(`/api/v1/pbs/pbs-servers/${serverId}`, data)
  },

  /**
   * Deleta servidor PBS
   */
  deleteServer: (serverId: string): Promise<void> => {
    return apiClient.delete(`/api/v1/pbs/pbs-servers/${serverId}`)
  },

  /**
   * Testa conexão com servidor PBS pelo agent de um cluster.
   *
   * O cluster é obrigatório: o backend não abre mais HTTP para o PBS, quem
   * chega lá é o agent. Testar por um cluster específico é também o que faz o
   * teste dizer algo — é o caminho que o backup daquele cluster vai usar.
   */
  testConnection: (serverId: string, clusterId: string): Promise<PBSConnectionTest> => {
    return apiClient.post(`/api/v1/pbs/pbs-servers/${serverId}/test-connection`, {
      cluster_id: clusterId,
    })
  },

  // ========================================
  // Datastores
  // ========================================

  /**
   * Lista datastores de um servidor PBS
   */
  listDatastores: (
    serverId: string,
    params?: {
      is_active?: boolean
      skip?: number
      limit?: number
    },
  ): Promise<PBSDatastore[]> => {
    return apiClient.get(`/api/v1/pbs/pbs-servers/${serverId}/datastores`, { params })
  },

  /**
   * Sincroniza datastores do servidor PBS.
   *
   * `clusterId` é opcional: sem ele a rota vem das associações do servidor (o
   * caminho normal). Com ele, o sync sai pelo agent daquele cluster mesmo sem
   * associação — necessário no primeiro sync de um PBS recém-cadastrado, que
   * ainda não foi associado a cluster nenhum.
   */
  syncDatastores: (serverId: string, clusterId?: string): Promise<SyncDatastoresResponse> => {
    return apiClient.post(
      `/api/v1/pbs/pbs-servers/${serverId}/sync-datastores`,
      clusterId ? { cluster_id: clusterId } : {},
    )
  },

  /**
   * Obtém detalhes de datastore
   */
  getDatastore: (datastoreId: string): Promise<PBSDatastore> => {
    return apiClient.get(`/api/v1/pbs/datastores/${datastoreId}`)
  },

  /**
   * Atualiza datastore
   */
  updateDatastore: (datastoreId: string, data: UpdateDatastoreRequest): Promise<PBSDatastore> => {
    return apiClient.put(`/api/v1/pbs/datastores/${datastoreId}`, data)
  },

  /**
   * Obtém resumo de uso do datastore
   */
  getDatastoreUsage: (datastoreId: string): Promise<DatastoreUsageSummary> => {
    return apiClient.get(`/api/v1/pbs/datastores/${datastoreId}/usage`)
  },

  // ========================================
  // Cluster Associations
  // ========================================

  /**
   * Lista associações de cluster com PBS
   */
  listClusterAssociations: (params?: {
    cluster_id?: string
    pbs_server_id?: string
    is_active?: boolean
    skip?: number
    limit?: number
  }): Promise<ClusterPBSAssociation[]> => {
    return apiClient.get('/api/v1/pbs/cluster-associations', { params })
  },

  /**
   * Associa cluster com servidor PBS
   */
  associateCluster: (data: AssociateClusterPBSRequest): Promise<ClusterPBSAssociation> => {
    return apiClient.post('/api/v1/pbs/cluster-associations', data)
  },

  /**
   * Remove associação cluster-PBS
   */
  removeClusterAssociation: (associationId: string): Promise<void> => {
    return apiClient.delete(`/api/v1/pbs/cluster-associations/${associationId}`)
  },

  // ========================================
  // Operations
  // ========================================

  /**
   * Executa garbage collection em datastore
   */
  runGarbageCollection: (
    serverId: string,
    data: RunGarbageCollectionRequest,
  ): Promise<{ task_upid: string; message: string }> => {
    return apiClient.post(`/api/v1/pbs/pbs-servers/${serverId}/run-gc`, data)
  },
}

// ========================================
// Helper Functions
// ========================================

/**
 * Formata tamanho em bytes para formato legível
 */
export const formatStorageSize = (bytes?: number): string => {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`
}

/**
 * Calcula porcentagem de utilização
 */
export const calculateUtilizationPercentage = (used?: number, total?: number): number => {
  if (!total || !used) return 0
  return (used / total) * 100
}

/**
 * Obtém cor do status de conexão
 */
export const getConnectionStatusColor = (isActive: boolean, lastSync?: string): string => {
  if (!isActive) return 'text-gray-600 bg-gray-100'

  if (!lastSync) return 'text-yellow-600 bg-yellow-100'

  const syncDate = new Date(lastSync)
  const hoursSinceSync = (Date.now() - syncDate.getTime()) / (1000 * 60 * 60)

  if (hoursSinceSync > 24) return 'text-orange-600 bg-orange-100'
  if (hoursSinceSync > 6) return 'text-yellow-600 bg-yellow-100'
  return 'text-green-600 bg-green-100'
}

/**
 * Obtém label do status de conexão
 */
export const getConnectionStatusLabel = (isActive: boolean, lastSync?: string): string => {
  if (!isActive) return 'Inativo'

  if (!lastSync) return 'Não sincronizado'

  const syncDate = new Date(lastSync)
  const hoursSinceSync = (Date.now() - syncDate.getTime()) / (1000 * 60 * 60)

  if (hoursSinceSync > 24) return 'Sincronização atrasada'
  if (hoursSinceSync > 6) return 'Sincronização antiga'
  return 'Conectado'
}

/**
 * Valida expressão cron
 */
export const isValidCronExpression = (expression: string): boolean => {
  if (!expression) return false

  // Validação básica: 5 ou 6 campos separados por espaço
  const parts = expression.trim().split(/\s+/)
  if (parts.length < 5 || parts.length > 6) return false

  // Cada parte deve conter apenas números, *, /, - ou vírgulas
  const validPattern = /^[\d*,\-/]+$/
  return parts.every((part) => validPattern.test(part))
}

/**
 * Obtém descrição legível de expressão cron
 */
export const describeCronExpression = (expression: string): string => {
  if (!expression) return 'Não configurado'

  const parts = expression.trim().split(/\s+/)
  if (parts.length < 5) return expression

  const [minute, hour, day, month, weekday] = parts

  // Padrões comuns
  if (expression === '0 2 * * *') return 'Diariamente às 02:00'
  if (expression === '0 0 * * 0') return 'Semanalmente aos domingos à meia-noite'
  if (expression === '0 0 1 * *') return 'Mensalmente no dia 1 à meia-noite'
  if (expression === '*/15 * * * *') return 'A cada 15 minutos'
  if (expression === '0 */6 * * *') return 'A cada 6 horas'

  // Descrição genérica
  let description = ''

  if (minute === '*') description += 'A cada minuto'
  else if (minute.startsWith('*/')) description += `A cada ${minute.slice(2)} minutos`
  else description += `No minuto ${minute}`

  if (hour !== '*') {
    if (hour.startsWith('*/')) description += `, a cada ${hour.slice(2)} horas`
    else description += `, às ${hour}h`
  }

  if (day !== '*') description += `, no dia ${day}`
  if (month !== '*') description += `, no mês ${month}`
  if (weekday !== '*') description += `, no dia da semana ${weekday}`

  return description || expression
}

/**
 * Formata timestamp para exibição
 */
export const formatTimestamp = (timestamp?: string): string => {
  if (!timestamp) return 'Nunca'

  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMins < 1) return 'Agora'
  if (diffMins < 60) return `${diffMins} minutos atrás`
  if (diffHours < 24) return `${diffHours} horas atrás`
  if (diffDays < 7) return `${diffDays} dias atrás`

  return date.toLocaleDateString('pt-BR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Obtém cor de severidade da utilização de armazenamento
 */
export const getStorageUtilizationColor = (percentage: number): string => {
  if (percentage >= 90) return 'text-red-600 bg-red-100'
  if (percentage >= 75) return 'text-orange-600 bg-orange-100'
  if (percentage >= 50) return 'text-yellow-600 bg-yellow-100'
  return 'text-green-600 bg-green-100'
}

/**
 * Valida token de API PBS (formato: usuario@pbs!token_id)
 */
export const isValidPBSTokenId = (tokenId: string): boolean => {
  if (!tokenId) return false

  // Formato esperado: usuario@pbs!token_id
  const pattern = /^[\w-]+@[\w-]+![\w-]+$/
  return pattern.test(tokenId)
}

/**
 * Obtém prioridade de associação em texto
 */
export const getPriorityLabel = (priority: number): string => {
  if (priority <= 50) return 'Alta'
  if (priority <= 100) return 'Normal'
  if (priority <= 500) return 'Baixa'
  return 'Muito baixa'
}

/**
 * Obtém cor da prioridade
 */
export const getPriorityColor = (priority: number): string => {
  if (priority <= 50) return 'text-green-600 bg-green-100'
  if (priority <= 100) return 'text-blue-600 bg-blue-100'
  if (priority <= 500) return 'text-yellow-600 bg-yellow-100'
  return 'text-gray-600 bg-gray-100'
}
